import { COLORS } from "@coeff/utils";
import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

interface ShowMoreProps {
  children: React.ReactNode;
  maxLines?: number;
}

const ExpandableContent = styled.div<{
  isExpanded: boolean;
  maxLines: number;
}>`
  overflow: hidden;
  ${({ isExpanded, maxLines }) =>
    !isExpanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: ${maxLines};
    -webkit-box-orient: vertical;
  `}
`;

const ShowMoreLink = styled.span`
  color: ${COLORS.black85};
  cursor: pointer;
  font-size: 12px;
  margin-top: 4px;
  display: inline-block;
  &:hover {
    text-decoration: underline;
  }
`;

export const ShowMore: React.FC<ShowMoreProps> = ({ children, maxLines = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setIsExpanded(false);
  }, [children, maxLines]);

  useLayoutEffect(() => {
    if (!contentRef.current || isExpanded) {
      return;
    }
    const { scrollHeight, clientHeight } = contentRef.current;
    setOverflows(scrollHeight > clientHeight);
  }, [children, maxLines, isExpanded]);

  return (
    <>
      <ExpandableContent ref={contentRef} isExpanded={isExpanded} maxLines={maxLines}>
        {children}
      </ExpandableContent>
      {overflows && (
        <ShowMoreLink onClick={() => setIsExpanded(prev => !prev)}>
          {isExpanded ? "Show less" : "Show more"}
        </ShowMoreLink>
      )}
    </>
  );
};
